// Client-side helpers for the organiser event submission surfaces.
//
// The validation here mirrors the server rules in api/lib/organiser-events.js
// (validateSubmission) field for field, so the form can surface friendly inline
// errors BEFORE a network round trip. The server remains authoritative: it
// re-validates every submission, so this is a UX convenience, not a security
// boundary. validateSubmitEventForm is a pure function (no React, no network)
// and is exercised directly by organiserSubmissions.test.ts.

import type { Event } from '../types';
import { stateTimeZone, stateCompatibleTimeZone } from './auTimezones';

export { STATE_TIME_ZONES, stateTimeZone } from './auTimezones';

// The eight Australian state and territory codes, with display labels. Mirrors
// AU_STATES in api/lib/organiser-events.js.
export const AU_STATE_OPTIONS: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'ACT', label: 'Australian Capital Territory' },
  { code: 'NSW', label: 'New South Wales' },
  { code: 'NT', label: 'Northern Territory' },
  { code: 'QLD', label: 'Queensland' },
  { code: 'SA', label: 'South Australia' },
  { code: 'TAS', label: 'Tasmania' },
  { code: 'VIC', label: 'Victoria' },
  { code: 'WA', label: 'Western Australia' },
];

export const AU_STATE_CODES: string[] = AU_STATE_OPTIONS.map((option) => option.code);

const AU_STATE_LABELS: Record<string, string> = AU_STATE_OPTIONS.reduce(
  (accumulator, option) => {
    accumulator[option.code] = option.label;
    return accumulator;
  },
  {} as Record<string, string>
);

export const stateLabel = (code: string | null | undefined): string => (
  code && AU_STATE_LABELS[code] ? AU_STATE_LABELS[code] : (code || '')
);

// IANA time zone for each state and territory. Event times are VENUE-local: a
// submitter in Perth entering 18:00 for a NSW event means 18:00 in Sydney, not
// 18:00 in Perth, so date-time conversion must use the event state's zone, not
// the browser's.
// Supported games with human-friendly labels. The value set mirrors GAMES in
// api/lib/organiser-events.js exactly (order included).
export const GAME_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'pokemon', label: 'Pokemon' },
  { value: 'onepiece', label: 'One Piece' },
  { value: 'magic', label: 'Magic: The Gathering' },
  { value: 'riftbound', label: 'Riftbound' },
  { value: 'yugioh', label: 'Yu-Gi-Oh!' },
  { value: 'lorcana', label: 'Disney Lorcana' },
  { value: 'fleshandblood', label: 'Flesh and Blood' },
  { value: 'other', label: 'Other' },
];

export const GAME_VALUES: string[] = GAME_OPTIONS.map((option) => option.value);

// Public self-serve submissions are shows only. These values preserve the
// existing backend column contract without exposing game or tournament fields
// in the public form.
export const PUBLIC_SHOW_GAME = 'other';
export const PUBLIC_SHOW_EVENT_TYPE = 'show';
export const PUBLIC_SHOW_EVENT_TYPES = [
  PUBLIC_SHOW_EVENT_TYPE,
  'card show',
  'tcg show',
  'trading card show',
  'convention',
  'collector event',
  'collectors event',
  'expo',
] as const;

const GAME_LABELS: Record<string, string> = GAME_OPTIONS.reduce(
  (accumulator, option) => {
    accumulator[option.value] = option.label;
    return accumulator;
  },
  {} as Record<string, string>
);

export const gameLabel = (value: string | null | undefined): string => (
  value && GAME_LABELS[value] ? GAME_LABELS[value] : (value || '')
);

// Length caps (characters, after sanitisation). Mirrors FIELD_LIMITS in
// api/lib/organiser-events.js.
export const FIELD_LIMITS = {
  title: 200,
  venue_name: 200,
  suburb: 120,
  address: 300,
  event_type: 100,
  cost: 100,
  description: 2000,
  registration_url: 2000,
  image_url: 2000,
  contact_email: 255,
  review_note: 1000,
} as const;

// Submission statuses, mirroring SUBMISSION_STATUSES in the backend.
export type SubmissionStatus = 'pending' | 'approved' | 'rejected' | 'void';

export const SUBMISSION_STATUS_LABELS: Record<SubmissionStatus, string> = {
  pending: 'Pending review',
  approved: 'Approved',
  rejected: 'Rejected',
  void: 'Withdrawn',
};

// The submission row shape returned by the organiser and admin endpoints
// (SUBMISSION_COLUMNS in api/routes/organiser.js / api/routes/admin.js).
export interface Submission {
  id: number;
  submitted_by: string;
  contact_email: string | null;
  title: string;
  game: string;
  event_type: string | null;
  description: string | null;
  venue_name: string;
  address: string | null;
  suburb: string | null;
  state: string;
  start_at: string | null;
  end_at: string | null;
  cost: string | null;
  registration_url: string | null;
  image_url: string | null;
  status: SubmissionStatus;
  review_note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  revision?: string;
  time_zone?: string | null;
}

export const isPublicShowSubmission = (submission: Pick<Submission, 'game' | 'event_type'>): boolean => (
  String(submission.game || '').trim().toLowerCase() === PUBLIC_SHOW_GAME
  && (PUBLIC_SHOW_EVENT_TYPES as readonly string[])
    .includes(String(submission.event_type || '').trim().toLowerCase())
);

// The form's local value shape (camelCase, all strings, as held in component
// state). buildSubmissionPayload maps it to the snake_case API body.
export interface SubmitEventFormValues {
  title: string;
  description: string;
  venueName: string;
  address: string;
  suburb: string;
  state: string;
  startAt: string;
  endAt: string;
  cost: string;
  registrationUrl: string;
  imageUrl: string;
  contactEmail: string;
  timeZone: string;
}

export const EMPTY_SUBMIT_EVENT_FORM: SubmitEventFormValues = {
  title: '',
  description: '',
  venueName: '',
  address: '',
  suburb: '',
  state: '',
  startAt: '',
  endAt: '',
  cost: '',
  registrationUrl: '',
  imageUrl: '',
  contactEmail: '',
  timeZone: '',
};

export type SubmitEventErrors = Partial<Record<keyof SubmitEventFormValues, string>>;

// Text sanitisation mirroring sanitizeText in api/lib/organiser-events.js: strip
// HTML tags, drop control characters, collapse whitespace and trim. We validate
// lengths and required-ness against the sanitised value so the client agrees
// with what the server will store (for example "<b></b>" sanitises to empty and
// therefore fails a required check on both sides).
export const sanitizeText = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const withoutTags = String(value).replace(/<[^>]*>/g, ' ');
  const withoutControl = Array.from(withoutTags)
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code > 31 && code !== 127;
    })
    .join('');
  return withoutControl.replace(/\s+/g, ' ').trim();
};

// http(s) only, mirroring isValidHttpUrl in the backend.
const isValidHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const isValidImageUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    const hostname = url.hostname.toLowerCase().replace(/\.$/, '');
    const isLocalName = hostname === 'localhost'
      || hostname.endsWith('.localhost')
      || hostname.endsWith('.local')
      || hostname.endsWith('.internal')
      || hostname.endsWith('.lan')
      || hostname.endsWith('.home.arpa')
      || hostname.endsWith('.test')
      || hostname.endsWith('.invalid')
      || hostname.endsWith('.onion');
    const isIpLiteral = /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)
      || hostname.includes(':');
    return url.protocol === 'https:'
      && !url.username
      && !url.password
      && Boolean(hostname)
      && hostname.includes('.')
      && !isLocalName
      && !isIpLiteral;
  } catch {
    return false;
  }
};

// Mirrors isValidEmail in the backend: a single @ with non-space text either
// side and a dot in the domain.
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const parseDate = (value: string): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

// The zone's UTC offset (in ms) at a given instant, computed with
// Intl.DateTimeFormat formatToParts (no external dependency): format the
// instant in the target zone, reassemble that wall time as if it were UTC, and
// the difference is the offset.
const zoneOffsetMs = (zone: string, epochMs: number): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(epochMs));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value || 0);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtc - epochMs;
};

const formatOffset = (offsetMs: number): string => {
  const sign = offsetMs < 0 ? '-' : '+';
  const abs = Math.abs(offsetMs);
  const hours = Math.floor(abs / 3600000);
  const minutes = Math.floor((abs % 3600000) / 60000);
  return `${sign}${pad(hours)}:${pad(minutes)}`;
};

const DAY_MS = 86400000;

// Candidate epochs whose zone offset reproduces the given wall time. Offsets
// are sampled a day either side of the wall time as well as at it, so both the
// pre- and post-transition offsets are considered around a DST changeover. Two
// results mean the wall time is in a DST fold; none mean it is in a DST gap.
const collectZoneCandidates = (zone: string, wallUtc: number): number[] => {
  const candidates: number[] = [];
  const seen = new Set<number>();
  const probes = [wallUtc - DAY_MS, wallUtc, wallUtc + DAY_MS];
  for (const probe of probes) {
    const offset = zoneOffsetMs(zone, probe);
    if (seen.has(offset)) continue;
    seen.add(offset);
    const candidate = wallUtc - offset;
    if (zoneOffsetMs(zone, candidate) === offset) candidates.push(candidate);
  }
  return candidates;
};

export interface ZonedIsoResult {
  // ISO 8601 with the venue zone's explicit offset, e.g. 2026-08-01T18:00:00+10:00
  iso: string;
  // true when the entered wall time fell inside a daylight saving gap and was
  // nudged forward one hour.
  adjusted: boolean;
}

// Interpret a datetime-local input value ("YYYY-MM-DDTHH:MM", optionally with
// seconds) as a WALL time in the given IANA zone and return the ISO instant
// with that zone's offset. Handles the two DST edge cases:
//   - gap (clocks spring forward, the wall time never exists): nudge the wall
//     time forward one hour and flag adjusted so the UI can say so;
//   - fold (clocks fall back, the wall time happens twice): take the FIRST
//     occurrence (the earlier instant).
export const localInputToZonedIso = (value: string, zone: string): ZonedIsoResult | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value || '');
  if (!match) return null;

  const [year, month, day, hour, minute, second] = [
    match[1], match[2], match[3], match[4], match[5], match[6] || '0',
  ].map(Number);
  let wallUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  if (Number.isNaN(wallUtc)) return null;

  let adjusted = false;
  let epoch: number | null = null;

  // At most two passes: the second only runs when the wall time sits in a DST
  // gap and has been nudged forward one hour (a nudged time always resolves).
  for (let attempt = 0; attempt < 2 && epoch === null; attempt += 1) {
    const candidates = collectZoneCandidates(zone, wallUtc);
    if (candidates.length > 0) {
      // Fold: both offsets are valid; the smaller epoch is the first occurrence.
      epoch = Math.min(...candidates);
    } else {
      // Gap: no valid offset reproduces this wall time; nudge forward one hour.
      wallUtc += 3600000;
      adjusted = true;
    }
  }

  if (epoch === null) return null;

  const offset = zoneOffsetMs(zone, epoch);
  const wall = new Date(epoch + offset);
  const iso = `${pad(wall.getUTCFullYear(), 4)}-${pad(wall.getUTCMonth() + 1)}-${pad(wall.getUTCDate())}`
    + `T${pad(wall.getUTCHours())}:${pad(wall.getUTCMinutes())}:${pad(wall.getUTCSeconds())}`
    + formatOffset(offset);
  return { iso, adjusted };
};

// The inverse of localInputToZonedIso: format a stored instant as the WALL
// time in the given IANA zone, in the "YYYY-MM-DDTHH:MM" shape a
// datetime-local input expects. Used to prefill the edit form so a Perth admin
// editing a NSW show sees the venue-local time that was submitted.
export const zonedIsoToLocalInput = (value: string | null | undefined, zone: string | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  if (!zone) {
    return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
      + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(date);
    const get = (type: string) => parts.find((part) => part.type === type)?.value || '';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
  } catch {
    return '';
  }
};

// Map a stored submission row back to the form's local value shape, converting
// stored instants to venue-local wall times. The inverse of
// buildSubmissionPayload for every editable field.
export const submissionToFormValues = (submission: Submission): SubmitEventFormValues => {
  const zone = stateCompatibleTimeZone(submission.state, submission.time_zone);
  return {
    title: submission.title || '',
    description: submission.description || '',
    venueName: submission.venue_name || '',
    address: submission.address || '',
    suburb: submission.suburb || '',
    state: submission.state || '',
    startAt: zonedIsoToLocalInput(submission.start_at, zone),
    endAt: zonedIsoToLocalInput(submission.end_at, zone),
    cost: submission.cost || '',
    registrationUrl: submission.registration_url || '',
    imageUrl: submission.image_url || '',
    contactEmail: submission.contact_email || '',
    timeZone: zone || '',
  };
};

// Short zone name (AEST, AEDT, ACST, AWST...) for an instant in a zone.
export const zoneAbbreviation = (zone: string, date: Date): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: zone,
      timeZoneName: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(date);
    return parts.find((part) => part.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
};

// Format a stored timestamp for display. When a state code is given the time
// is rendered in the VENUE's zone with its abbreviation appended (so reviewers
// and submitters see venue-local times unambiguously); without one it falls
// back to the browser's zone with no label.
export const formatSubmissionTime = (
  value: string | null | undefined,
  stateCode?: string | null
): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const zone = stateCode ? stateTimeZone(stateCode) : undefined;
  try {
    const formatted = new Intl.DateTimeFormat('en-AU', {
      dateStyle: 'medium',
      timeStyle: 'short',
      ...(zone ? { timeZone: zone } : {}),
    }).format(date);
    if (!zone) return formatted;
    const abbreviation = zoneAbbreviation(zone, date);
    return abbreviation ? `${formatted} ${abbreviation}` : formatted;
  } catch {
    return date.toLocaleString('en-AU');
  }
};

// Validate the submit-event form, returning a map of field -> friendly message.
// An empty object means the form passes the client checks. opts.now is the
// reference time for the "start must be in the future" rule and is injectable so
// the test is deterministic.
export const validateSubmitEventForm = (
  values: SubmitEventFormValues,
  opts: { now?: Date } = {}
): SubmitEventErrors => {
  const now = opts.now instanceof Date ? opts.now : new Date();
  const errors: SubmitEventErrors = {};

  const title = sanitizeText(values.title);
  const venueName = sanitizeText(values.venueName);
  const suburb = sanitizeText(values.suburb);
  const address = sanitizeText(values.address);
  const cost = sanitizeText(values.cost);
  const description = sanitizeText(values.description);
  const state = (values.state || '').trim().toUpperCase();
  const registrationUrl = (values.registrationUrl || '').trim();
  const imageUrl = (values.imageUrl || '').trim();
  const contactEmail = (values.contactEmail || '').trim();

  // Required fields.
  if (!title) {
    errors.title = 'Please enter a show title.';
  } else if (title.length > FIELD_LIMITS.title) {
    errors.title = `Title must be ${FIELD_LIMITS.title} characters or fewer.`;
  }

  if (!venueName) {
    errors.venueName = 'Please enter the venue name.';
  } else if (venueName.length > FIELD_LIMITS.venue_name) {
    errors.venueName = `Venue name must be ${FIELD_LIMITS.venue_name} characters or fewer.`;
  }

  if (!state) {
    errors.state = 'Please choose a state or territory.';
  } else if (!AU_STATE_CODES.includes(state)) {
    errors.state = 'Please choose a state or territory from the list.';
  }

  // Optional text fields: length caps only.
  if (suburb.length > FIELD_LIMITS.suburb) {
    errors.suburb = `Suburb must be ${FIELD_LIMITS.suburb} characters or fewer.`;
  }
  if (address.length > FIELD_LIMITS.address) {
    errors.address = `Address must be ${FIELD_LIMITS.address} characters or fewer.`;
  }
  if (cost.length > FIELD_LIMITS.cost) {
    errors.cost = `Cost must be ${FIELD_LIMITS.cost} characters or fewer.`;
  }
  if (description.length > FIELD_LIMITS.description) {
    errors.description = `Description must be ${FIELD_LIMITS.description} characters or fewer.`;
  }

  // Dates. Wall times are interpreted in the event STATE's zone when the state
  // is valid (venue-local semantics); otherwise fall back to the browser zone,
  // which only happens while the state field itself is still in error.
  const zone = AU_STATE_CODES.includes(state) ? stateCompatibleTimeZone(state, values.timeZone) : undefined;
  const parseFormDateTime = (raw: string): Date | null => {
    if (!raw) return null;
    if (zone) {
      const zoned = localInputToZonedIso(raw, zone);
      if (zoned) return new Date(zoned.iso);
    }
    return parseDate(raw);
  };

  const startAt = parseFormDateTime(values.startAt);
  if (!values.startAt) {
    errors.startAt = 'Please choose a start date and time.';
  } else if (!startAt) {
    errors.startAt = 'That start date and time is not valid.';
  } else if (startAt.getTime() <= now.getTime()) {
    errors.startAt = 'The start date and time must be in the future.';
  }

  if (values.endAt) {
    const endAt = parseFormDateTime(values.endAt);
    if (!endAt) {
      errors.endAt = 'That end date and time is not valid.';
    } else if (startAt && endAt.getTime() < startAt.getTime()) {
      errors.endAt = 'The end date and time cannot be before the start.';
    }
  }

  // Optional URL and email.
  if (registrationUrl) {
    if (!isValidHttpUrl(registrationUrl)) {
      errors.registrationUrl = 'Enter a valid link starting with http:// or https://';
    } else if (registrationUrl.length > FIELD_LIMITS.registration_url) {
      errors.registrationUrl = `Link must be ${FIELD_LIMITS.registration_url} characters or fewer.`;
    }
  }

  if (imageUrl) {
    if (!isValidImageUrl(imageUrl)) {
      errors.imageUrl = 'Enter a public https:// image link on a public host without a username or password';
    } else if (imageUrl.length > FIELD_LIMITS.image_url) {
      errors.imageUrl = `Image link must be ${FIELD_LIMITS.image_url} characters or fewer.`;
    }
  }

  if (contactEmail) {
    if (!isValidEmail(contactEmail)) {
      errors.contactEmail = 'Enter a valid email address.';
    } else if (contactEmail.length > FIELD_LIMITS.contact_email) {
      errors.contactEmail = `Email must be ${FIELD_LIMITS.contact_email} characters or fewer.`;
    }
  }

  return errors;
};

// Map the form values to the snake_case JSON body the API expects. Date-time
// wall values are interpreted in the event state's zone and sent as ISO strings
// with that zone's explicit offset, so a Perth submitter entering 18:00 for a
// NSW event stores 18:00 Sydney time. The server re-validates and sanitises
// everything. Empty optional fields are sent as '' and treated as absent.
export const buildSubmissionPayload = (values: SubmitEventFormValues) => {
  const zone = stateCompatibleTimeZone(values.state, values.timeZone);
  const toApiDate = (raw: string): string => {
    if (!raw) return '';
    if (zone) {
      const zoned = localInputToZonedIso(raw, zone);
      if (zoned) return zoned.iso;
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? raw : parsed.toISOString();
  };

  return {
    title: values.title,
    game: PUBLIC_SHOW_GAME,
    event_type: PUBLIC_SHOW_EVENT_TYPE,
    description: values.description,
    venue_name: values.venueName,
    address: values.address,
    suburb: values.suburb,
    state: values.state,
    start_at: toApiDate(values.startAt),
    end_at: toApiDate(values.endAt),
    cost: values.cost,
    registration_url: values.registrationUrl,
    image_url: values.imageUrl,
    contact_email: values.contactEmail,
  };
};

// Build the same event shape the approved organiser feed publishes, allowing
// the submission form to reuse the real EventModal presentation without any
// network request or durable event id.
export const buildSubmissionPreviewEvent = (values: SubmitEventFormValues): Event => {
  const payload = buildSubmissionPayload(values);
  const state = values.state.trim().toUpperCase();
  const eventType = 'Show';
  const venueName = sanitizeText(values.venueName);
  const location = [venueName, sanitizeText(values.address), sanitizeText(values.suburb), stateLabel(state)]
    .filter(Boolean)
    .join(', ');
  const tags = [eventType, state].filter(Boolean);

  return {
    id: '',
    title: sanitizeText(values.title),
    description: sanitizeText(values.description),
    start: payload.start_at,
    end: payload.end_at,
    url: values.registrationUrl.trim(),
    extendedProps: {
      location,
      address: location,
      descriptionUrl: values.registrationUrl.trim(),
      ticketCost: sanitizeText(values.cost),
      tags,
      image: values.imageUrl.trim(),
      shop: venueName,
      format: eventType,
      region: stateLabel(state),
      stateCode: state,
      eventType,
      timeZone: stateCompatibleTimeZone(state, values.timeZone) || '',
    },
  };
};

// Sanitise-then-validate a reject review note, mirroring api/routes/admin.js
// exactly: sanitizeText first, then required and length checks on the sanitised
// value, and the SANITISED note is what gets sent. A note of pure HTML or
// whitespace is empty after sanitisation and fails the required check on both
// sides.
export const prepareReviewNote = (raw: string): { note: string; error: string | null } => {
  const note = sanitizeText(raw);
  if (!note) {
    return { note: '', error: 'A note is required so the organiser knows why the event was rejected.' };
  }
  if (note.length > FIELD_LIMITS.review_note) {
    return { note, error: `The note must be ${FIELD_LIMITS.review_note} characters or fewer.` };
  }
  return { note, error: null };
};

// Base URL and auth header helpers, matching the established pattern in
// AuthContext, Settings and EventModal.
export const getApiBase = (): string => process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const authHeaders = (token: string | null): Record<string, string> => ({
  Authorization: `Bearer ${token || ''}`,
  'Content-Type': 'application/json',
});
